import { inject, Injectable } from "@angular/core";
import { DxFormComponent, DxFormTypes } from "devextreme-angular/ui/form";
import { sntTranslate } from "src/app/support/translate";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { ColorService, ColorWithName } from "src/app/services/color.service";
// @ts-ignore
import { el, mount } from "src/app/support/dom";
import { PopupService } from "src/app/services/popup/popup.service";

type Input = {
  name?: string;
};

type Providers = {
  createTagFn: (data: PopupData) => Promise<void>;
};

type PopupData = {
  name: string;
  color: string;
};

@Injectable({
  providedIn: "root"
})
export class CreateTagFunctionService {
  private readonly popupService = inject(PopupService);
  private readonly colorService = inject(ColorService);

  private readonly defaultColor: string = "#a79b8e";

  async execute(input: Input, providers: Providers): Promise<void> {
    let dxForm: DxFormComponent | undefined;

    await this.popupService.createEntity<PopupData>({
      title: sntTranslate(marker("Nuovo tag")),
      modalConfig: {
        height: 80,
        width: 565
      },
      initialFormData: {
        name: input.name,
        color: this.defaultColor
      },
      onFormReady: (formReadyEvent) => {
        dxForm = formReadyEvent.dxForm;

        dxForm.instance.on(
          "fieldDataChanged",
          (dxEvent: DxFormTypes.FieldDataChangedEvent) => {
            dxForm?.instance.updateData({
              colorTemplate: dxEvent.value
            });

            dxForm?.instance.repaint();
          }
        );
      },
      formItems: [
        {
          itemType: "group",
          colCount: 11,
          items: [
            {
              dataField: "name",
              colSpan: 5,
              label: {
                text: sntTranslate(marker("Nome")),
                location: "top",
                showColon: false
              },
              validationRules: [{ type: "required" }],
              editorType: "dxTextBox"
            },
            {
              dataField: "color",
              colSpan: 5,
              label: {
                text: sntTranslate(marker("Colore")),
                location: "top",
                showColon: false
              },
              validationRules: [{ type: "required" }],
              editorType: "dxSelectBox",
              editorOptions: {
                displayExpr: "name",
                valueExpr: "color",
                showClearButton: false,
                dataSource: this.colorService.listWithName(),
                dropDownOptions: {},
                itemTemplate: (
                  colorWithName: ColorWithName,
                  index: number,
                  htmlElement: HTMLElement
                ) => {
                  mount(
                    htmlElement,
                    el(
                      "div",
                      {
                        class: "flex-1 flex flex-row items-center gap-2",
                        style: "align-self: stretch"
                      },
                      [
                        el("div", {
                          class: "rounded-md aspect-square h-[34px]",
                          style: {
                            ["background-color"]: colorWithName.color,
                            ["aspect-ratio"]: "1"
                          }
                        }),
                        el(
                          "div",
                          el(
                            "span",
                            { class: "text-xs text-slate-900" },
                            sntTranslate(colorWithName.name)
                          )
                        )
                      ]
                    )
                  );

                  return htmlElement;
                }
              }
            },
            {
              dataField: "colorTemplate",
              colSpan: 1,
              template: (a: any, b: any, c: any) => {
                if (!dxForm?.formData.color) {
                  return el("div");
                }

                const color = dxForm.formData.color;

                return el(
                  "div",
                  el("div", {
                    style: {
                      backgroundColor: color,
                      height: "34px",
                      marginTop: "22px"
                    },
                    class: "rounded-md aspect-square"
                  })
                );
              },
              label: {
                location: "top",
                visible: false
              }
            }
          ]
        }
      ],
      create: async (data) => {
        await providers.createTagFn(data);

        dxForm?.instance.off("sfieldDataChanged");
      },
      onCancel: async () => {
        dxForm?.instance.off("fieldDataChanged");
      }
    });
  }
}
